import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ImageUploader } from './MenuFormControls';
import { Sparkles, Plus, Trash2, Store, Save, Loader2, Megaphone, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Host venue branding panel. Lives on the Settings page.
 *
 * - Profile photo (shown on the QR landing page + menu hero)
 * - Offers list (free-text lines, max 10) — appear as a banner on the guest menu
 */
const VenueBrandingCard = () => {
  const [logoUrl, setLogoUrl] = useState('');
  const [wallpaperUrl, setWallpaperUrl] = useState('');
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Staff "custom shoutout" — posts straight to the TV, no guest / no approval.
  const [shoutName, setShoutName] = useState('');
  const [shoutMsg, setShoutMsg] = useState('');
  const [shoutKind, setShoutKind] = useState('shoutout');
  const [sending, setSending] = useState(false);

  const isCelebration = shoutKind === 'birthday' || shoutKind === 'anniversary';

  const sendShoutout = async () => {
    const name = shoutName.trim();
    const message = shoutMsg.trim();
    if (!name || !message) { toast.error('Add a name and a message'); return; }
    setSending(true);
    try {
      // `celebration_from` is auto-set server-side to "the <venue> team" for
      // birthday/anniversary takeovers — no separate "from" input needed.
      await axios.post(`${API}/shoutouts/custom`, {
        guest_name: name,
        message,
        kind: shoutKind,
      });
      toast.success(
        shoutKind === 'shoutout'
          ? '🎉 Shoutout sent to the TV screen!'
          : '🎉 Celebration takeover sent to the TV!',
      );
      setShoutName(''); setShoutMsg(''); setShoutKind('shoutout');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send shoutout');
    } finally {
      setSending(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/venue/profile`);
      setLogoUrl(r.data.venue_logo_url || '');
      setWallpaperUrl(r.data.venue_wallpaper_url || '');
      setOffers(r.data.offers || []);
    } catch (loadErr) {
      console.warn('[VenueBrandingCard] profile load failed:', loadErr?.response?.status, loadErr?.message || loadErr);
    }
    setLoading(false);
    setDirty(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateLogo = (url) => { setLogoUrl(url); setDirty(true); };
  const updateOffer = (idx, val) => {
    setOffers((prev) => prev.map((o, i) => (i === idx ? val : o)));
    setDirty(true);
  };
  const addOffer = () => {
    if (offers.length >= 10) { toast.error('Max 10 offers'); return; }
    setOffers([...offers, '']);
    setDirty(true);
  };
  const removeOffer = (idx) => {
    setOffers(offers.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = offers.map((o) => (o || '').trim()).filter(Boolean);
      const r = await axios.put(`${API}/venue/profile`, {
        venue_logo_url: logoUrl,
        venue_wallpaper_url: wallpaperUrl,
        offers: cleaned,
      });
      setLogoUrl(r.data.venue_logo_url || '');
      setWallpaperUrl(r.data.venue_wallpaper_url || '');
      setOffers(r.data.offers || []);
      setDirty(false);
      toast.success('Branding saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 text-[#FF007F] animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="venue-branding-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Store className="w-5 h-5 text-[#FF007F]" />
          <h3 className="text-sm font-extrabold text-white uppercase tracking-[0.18em] font-['Outfit']">
            Venue Branding
          </h3>
        </div>
        {dirty && (
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#FF007F] hover:bg-[#CC0066] text-white rounded-full px-4 h-8 text-[11px] font-bold tracking-wider"
            data-testid="venue-branding-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            SAVE
          </Button>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-xs text-gray-400 mb-2 block font-semibold tracking-wider uppercase">Profile photo</label>
          <p className="text-[11px] text-gray-500 mb-2 leading-relaxed">
            Shown on the guest QR landing page and on the menu hero.
          </p>
          <ImageUploader value={logoUrl} onChange={updateLogo} testIdPrefix="venue-logo" />
        </div>

        {/* Staff custom shoutout — straight to the TV, no guest / no approval */}
        <div className="rounded-xl border border-[#FF007F]/25 bg-[#FF007F]/[0.04] p-4" data-testid="staff-shoutout-box">
          <div className="flex items-center gap-2 mb-1.5">
            <Megaphone className="w-4 h-4 text-[#FF007F]" />
            <label className="text-xs text-white block font-bold tracking-wider uppercase">Send a shoutout to the TV</label>
          </div>
          <p className="text-[11px] text-gray-500 mb-3 leading-relaxed">
            Post a custom shoutout for a guest — it appears on the TV screen right away, no approval needed
            (stays up for ~10 minutes).
          </p>
          <div className="space-y-2">
            <Input
              value={shoutName}
              onChange={(e) => setShoutName(e.target.value.slice(0, 60))}
              placeholder={isCelebration ? "Who's it for? (e.g. Kabir)" : "Who's it for? (e.g. Table 7 — Priya)"}
              className="bg-black/40 border-white/10 text-white h-10 rounded-xl"
              data-testid="staff-shoutout-name"
            />
            {/* One-tap templates — prefill the message, staff can tweak after */}
            <div className="flex flex-wrap gap-1.5" data-testid="staff-shoutout-templates">
              {[
                { key: 'birthday', label: '🎂 Birthday', kind: 'birthday', text: 'Happy Birthday! 🎂 The whole bar is cheering for you — have an amazing night!' },
                { key: 'anniversary', label: '💞 Anniversary', kind: 'anniversary', text: 'Happy Anniversary! 💞 Here\u2019s to you two — cheers to love and great music!' },
                { key: 'congrats', label: '🎉 Congrats', kind: 'shoutout', text: 'Congratulations! 🎉 Let\u2019s celebrate — this one\u2019s for you!' },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => { setShoutMsg(t.text); setShoutKind(t.kind); }}
                  className="px-3 py-1.5 rounded-full text-[11px] font-bold bg-white/[0.05] ring-1 ring-white/10 text-gray-300 hover:text-white hover:ring-[#FF007F]/40 hover:bg-[#FF007F]/10 transition-all"
                  data-testid={`staff-shoutout-template-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {isCelebration && (
              <p className="text-[11px] text-gray-500 leading-relaxed" data-testid="staff-shoutout-from-note">
                The TV will sign this off automatically as <span className="text-[#FF007F] font-semibold">“From the venue team”</span>.
              </p>
            )}
            <textarea
              value={shoutMsg}
              onChange={(e) => setShoutMsg(e.target.value.slice(0, 240))}
              placeholder="Happy Birthday Priya! 🎂 The whole bar's cheering for you!"
              rows={2}
              className="w-full bg-black/40 border border-white/10 text-white rounded-xl p-3 text-sm resize-none focus:outline-none focus:border-[#FF007F]/40"
              data-testid="staff-shoutout-message"
            />
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-gray-600 font-mono">{shoutMsg.length}/240</span>
              <Button
                onClick={sendShoutout}
                disabled={sending || !shoutName.trim() || !shoutMsg.trim()}
                className="bg-[#FF007F] hover:bg-[#CC0066] text-white rounded-full px-4 h-9 text-[11px] font-bold tracking-wider disabled:opacity-50"
                data-testid="staff-shoutout-send"
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                SEND TO TV
              </Button>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-gray-400 block font-semibold tracking-wider uppercase">
              Offers <span className="text-gray-600 normal-case ml-1">({offers.length}/10)</span>
            </label>
            <Button
              onClick={addOffer}
              variant="ghost"
              className="text-[#39FF14] hover:bg-[#39FF14]/10 h-7 px-2.5 rounded-full text-[10px] font-bold tracking-wider"
              data-testid="venue-add-offer"
            >
              <Plus className="w-3 h-3 mr-1" /> ADD
            </Button>
          </div>
          <p className="text-[11px] text-gray-500 mb-3 leading-relaxed">
            One line per offer. Shown as a marquee banner on the guest menu screen.
          </p>
          {offers.length === 0 ? (
            <button
              onClick={addOffer}
              className="w-full p-3 rounded-xl border border-dashed border-white/10 hover:border-[#39FF14]/30 text-gray-500 hover:text-[#39FF14] text-xs transition-colors"
              data-testid="venue-add-first-offer"
            >
              + Add your first offer (e.g. &quot;Buy 1 Get 1 beer until 8 PM&quot;)
            </button>
          ) : (
            <div className="space-y-2">
              {offers.map((offer, idx) => (
                // key is idx alone — including `offer` in the key caused the
                // whole row (Input included) to unmount + remount on every
                // keystroke, dropping focus. Order is add/remove-only so
                // idx is stable enough.
                <div key={`offer-row-${idx}`} className="flex gap-2" data-testid={`venue-offer-row-${idx}`}>
                  <Sparkles className="w-3.5 h-3.5 text-[#39FF14] mt-3 flex-shrink-0" />
                  <Input
                    value={offer}
                    onChange={(e) => updateOffer(idx, e.target.value.slice(0, 200))}
                    placeholder="Buy 1 Get 1 beer until 8 PM"
                    className="bg-black/40 border-white/10 text-white h-10 rounded-xl flex-1"
                    data-testid={`venue-offer-input-${idx}`}
                  />
                  <Button
                    onClick={() => removeOffer(idx)}
                    variant="ghost"
                    className="h-10 w-10 p-0 rounded-xl text-[#FF3B3B] hover:bg-[#FF3B3B]/10"
                    title="Remove"
                    data-testid={`venue-offer-remove-${idx}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VenueBrandingCard;
